import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { unitApi } from '@/lib/api/services/unit.api';
import { useTenantList } from '@/hooks/useTenantLists';
import type { UnitFamilyGroup } from '@/types/unit';

const EMPTY: UnitFamilyGroup[] = [];

/**
 * Which units to hand back.
 *
 * `tenant` — what this kitchen said it works in (T-1006). Right for every
 * picker: a kitchen that never buys by the gallon should not be offered one.
 * `all` — the whole catalog, for the Configuration card that decides the filter
 * and therefore has to see what it has just switched off.
 */
type UnitScope = 'tenant' | 'all';

/**
 * The unit list is a constant compiled into the API, so it is fetched once and
 * never refetched. Callers get `[]` rather than `undefined` while it loads —
 * every consumer is a picker, and a picker with no options is the honest state.
 *
 * A unit the tenant has switched off is filtered out here rather than at each
 * call site, so there is one place that can be wrong. A value already stored on
 * a record survives the filter: `UnitSelect` appends whatever it is showing.
 */
export function useUnits(enabled = true, scope: UnitScope = 'tenant') {
  const query = useQuery({
    queryKey: ['units'],
    queryFn: () => unitApi.getAll(),
    enabled,
    staleTime: Infinity,
  });

  const chosen = useTenantList('units', enabled && scope === 'tenant');

  const groups = useMemo(() => {
    const all = query.data ?? EMPTY;
    // No answer yet is not the same as an empty list: while Configuration is
    // still loading, offer everything rather than nothing.
    if (scope === 'all' || !chosen) return all;
    const allowed = new Set(chosen);
    return all
      .map((group) => ({ ...group, units: group.units.filter((unit) => allowed.has(unit)) }))
      .filter((group) => group.units.length > 0);
  }, [query.data, chosen, scope]);

  return { ...query, groups };
}
